<template>
  <dynamic-input
    v-model="value"
    :options="filter"
    :no-label="noLabel"
    :class="version === 1 ? 'mr-4' : ''"
    :min-width="version === 1"
    :body-render="bodyRender"
    :datatable-id="datatableId"
  ></dynamic-input>
</template>

<script>
// Docs: app/javascript/components/DatatableFilter.md
import { isFalsy } from "@/lib/helpers.js";
import { trackMobileWebEvent } from "@/lib/pendo";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import DynamicInput from "@/components/DynamicInput.vue";
import CONSTANTS from "@/Constants.js";

export default {
  components: {
    DynamicInput
  },
  props: {
    filter: {
      type: Object,
      default: () => ({})
    },
    noLabel: {
      // passed through
      type: Boolean,
      default: false
    },
    version: {
      type: Number,
      default: 1
    },
    bodyRender: {
      type: Boolean,
      default: true
    },
    datatableId: {
      type: String,
      default: ""
    }
  },
  emits: ["refresh-table", "filter-applied", "filter-removed"],
  data() {
    return {
      value: this.filter.default_value,
      // Gate tracking emits until after the component has hydrated from URL
      // params or defaults, so page loads don't look like user interactions.
      readyToEmit: false
    };
  },
  computed: {
    paramName() {
      return this.datatableId
        ? `${this.datatableId}_${this.filter.name}`
        : this.filter.name;
    }
  },
  watch: {
    value(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.handleFilterChange(newValue);
      }
    }
  },
  async created() {
    await this.setInitialFilters();
    // Wait for any watchers queued by initial value assignment to flush
    // before allowing tracking emits; otherwise URL hydration would fire
    // a filter-applied event that looks like user interaction.
    await this.$nextTick();
    this.readyToEmit = true;
  },
  methods: {
    clearValue() {
      this.value = "";
    },
    async handleFilterChange(value) {
      // Handle query param parsing on page to prevent duplicate requests
      // should only refresh table if the filter is removed or has changed values
      if (isFalsy(value) || !this.isFilterApplied(value)) {
        await updateQueryParam(this.paramName, value, true);
        this.$emit("refresh-table");
        this.emitFilterChange(value);
      }
    },
    emitFilterChange(value) {
      if (!this.readyToEmit) return;
      if (isFalsy(value)) {
        this.$emit("filter-removed", { filter_name: this.filter.name });
        return;
      }
      const payload = {
        filter_name: this.filter.name,
        filter_value: value
      };
      if (Array.isArray(value)) {
        payload.filter_value_count = value.length;
      }
      this.$emit("filter-applied", payload);
      this.trackDateFilterApplied();
    },
    // Filtering by date is the primary report action and the measure this
    // work is meant to move, so it is tagged here rather than per report:
    // this is the one place both the column filters and DatatableFilterPopup
    // pass through, and the popup emits no tracking of its own.
    //
    // trackMobileWebEvent rather than trackEvent, because the initiative
    // splits every measure by layout and viewport width.
    trackDateFilterApplied() {
      const { date, dateRange } = CONSTANTS.REPORT_FILTER_TYPES;
      if (![date, dateRange].includes(this.filter.type)) return;

      trackMobileWebEvent("report_date_filter_applied", {
        filter_name: this.filter.name,
        filter_type: this.filter.type,
        surface: this.datatableId || "report"
      });
    },
    isFilterApplied(value) {
      // Check if filter is already applied
      const params = getQueryVariables();
      return params[this.paramName] == value;
    },
    async setInitialFilters() {
      const params = getQueryVariables();
      const paramValue = params[this.paramName];
      const hasParam = !isFalsy(paramValue);
      const hasDefault = !isFalsy(this.filter.default_value);
      const isV1 = this.version === 1;
      // URL-global drill-through sentinel: when a caller (e.g., the
      // Membership Health Dashboard) navigates a user to a report and
      // wants to bypass the report's filter defaults, it appends
      // `from_dashboard=1`. Without this the report would silently apply
      // defaults that don't match the upstream metric's predicate.
      const skipDefault = params.from_dashboard === "1";

      if (hasParam) {
        this.value = paramValue;
        return;
      }

      if (hasDefault && !skipDefault) {
        if (isV1) {
          await this.handleFilterChange(this.filter.default_value);
          const updated = getQueryVariables()[this.paramName];
          this.value = !isFalsy(updated) ? updated : this.filter.default_value;
          return;
        } else {
          this.value = this.filter.default_value;
          return;
        }
      }

      this.value = undefined;
    }
  }
};
</script>

<style lang="scss" scoped></style>
